/// FOURJS_START_COPYRIGHT(D,2021)
/// Property of Four Js*
/// (c) Copyright Four Js 2021, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("ClipboardService", ["InitService"], function (context, cls) {
  /**
   * @namespace gbc.ClipboardService
   * @gbcService
   */
  class ClipboardService /** @lends gbc.ClipboardService */ {
    constructor() {
      /** @type {Clipboard} */
      this._clipboard = null;
    }

    /**
     *  Init ClipboardService
     */
    init() {
      this._clipboard = navigator.clipboard;

      if (this._clipboard) {
        cls.DOMElementHelper.on(document.body, "paste.ClipboardService", this._handlePaste.bind(this));
        cls.DOMElementHelper.on(document.body, "copy.ClipboardService", this._handleCopy.bind(this));
        cls.DOMElementHelper.on(document.body, "cut.ClipboardService", this._handleCut.bind(this));
      }
    }

    /**
     * Handle Paste event
     * @param {ClipboardEvent} event
     * @private
     */
    _handlePaste(event) {
      context.LogService.clipboard.log("Paste event : ", event);

      if (!this._canProcessEvent()) {
        return;
      }

      // search widget from dom event
      const widget = gbc.WidgetService.getWidgetFromElement(event.target);
      if (!widget) {
        return;
      }
      if (!widget.handleClipboardEvent()) {
        return;
      }
      // Non-VM widgets must use native browser paste
      if (!widget.getAuiTag()) {
        return;
      }
      this.pasteToWidget(widget).then(null);

      // Cancel the native paste
      cls.EventHelper.preventCancelableDefault(event);
    }

    /**
     * Handle Copy event
     * @param {ClipboardEvent} event
     * @private
     */
    _handleCopy(event) {
      context.LogService.clipboard.log("Copy event : ", event);

      // search widget from dom event
      let widget = gbc.WidgetService.getWidgetFromElement(event.target);
      if (!widget) {
        return;
      }
      if (!widget.handleClipboardEvent()) {
        return;
      }

      gbc.ClipboardService.copyFromWidget(widget).then(null);

      // Cancel the native copy
      cls.EventHelper.preventCancelableDefault(event);
    }

    /**
     * Handle Cut event
     * @param {ClipboardEvent} event
     * @private
     */
    _handleCut(event) {
      context.LogService.clipboard.log("Cut event : ", event);

      // search widget from dom event
      let widget = gbc.WidgetService.getWidgetFromElement(event.target);

      // if a widget is found
      if (!widget) {
        return;
      }
      if (!widget.handleClipboardEvent()) {
        return;
      }
      // Non-VM widgets must use native browser cut
      if (!widget.getAuiTag()) {
        return;
      }
      this.cutFromWidget(widget);

      // Cancel the native cut
      cls.EventHelper.preventCancelableDefault(event);
    }

    /**
     * Paste content of clipboard in the given widget
     * @param {classes.WidgetBase} widget
     */
    async pasteToWidget(widget) {
      if (!this.isApiAvailable()) {
        return false;
      }

      const text = await this.getClipboardData();
      if (text === null) {
        return false;
      }
      context.SessionService.getCurrent().getCurrentApplication()?.scheduler.clipboardPasteCommand(text, widget);
      return true;
    }

    /**
     * Copy content of the widget to the clipboard
     * @param {classes.WidgetBase} widget
     * @param {?string} [forcedValue]
     * @param {Boolean} [ignoreSelection]
     */
    async copyFromWidget(widget, forcedValue = null, ignoreSelection = false) {
      if (!this.isApiAvailable()) {
        return;
      }

      let copyData = forcedValue;
      if (copyData === null) {
        copyData = widget.manageClipboardCopy(widget.getValueForClipboard(ignoreSelection));
      }
      context.LogService.clipboard.log(`Set clipboard from widget COPY ${widget._auiName}`);
      await this.setClipboardData(copyData);
    }

    /**
     * Copy all the widgets value for clipboard, in the order given, separated by tabulations
     * @param {classes.WidgetBase} invokerWidget
     * @param {Array<classes.WidgetBase>} widgets
     * @param {Boolean} [ignoreSelection]
     */
    async copyFromWidgets(invokerWidget, widgets, ignoreSelection = false) {
      if (!this.isApiAvailable()) {
        return;
      }

      // Go through all the widgets, get their values, format it
      const rawData = widgets.map((widget) => widget.getValueForClipboard(ignoreSelection)).join("\t");
      const data = invokerWidget.manageClipboardCopy(rawData);
      context.LogService.clipboard.log(`Set clipboard from widget COPY ${invokerWidget._auiName}`);
      await this.setClipboardData(data);
    }

    /**
     * Copy content of the widget to the clipboard
     * @param {classes.WidgetBase} widget
     */
    cutFromWidget(widget) {
      if (!this.isApiAvailable()) {
        return;
      }
      context.SessionService.getCurrent().getCurrentApplication().scheduler.clipboardCutCommand(widget);
    }

    /**
     * Check if clipboard API is available
     * @return {boolean}
     */
    isApiAvailable() {
      if (!this._clipboard) {
        context.LogService.clipboard.log(`API not available`);
      }

      return !!this._clipboard;
    }

    /**
     * @return {boolean} false if the application is waiting in background
     * @private
     */
    _canProcessEvent() {
      const application = context.SessionService.getCurrent()?.getCurrentApplication();

      if (application) {
        return application.canProcessEvent();
      }

      return true;
    }

    /**
     * Get the clipboard content
     * @param {boolean} [requireUserAction] If set to true, a message will be display
     * @returns {Promise<String>} A promise containing the clipboard data or null if a problem occurred
     */
    async getClipboardData(requireUserAction = true) {
      if (!this.isApiAvailable()) {
        return null;
      }

      let data = null;
      try {
        data = await this._clipboard.readText();
      } catch (err) {
        if (!requireUserAction) {
          return null;
        }
        context.LogService.clipboard.log(`Failed to get data: ${err}`);
        context.LogService.clipboard.log(`Retry to get data with an user action`);
        let success = false;
        await gbc.alert(gbc.I18NService.t("gwc.clipboard.getAccess"), null, async () => {
          try {
            data = await this._clipboard.readText();
            success = true;
          } catch (err) {
            context.LogService.clipboard.warn(`Failed to get data after retry: ${err}`);
          }
        });
        if (!success) {
          return null;
        }
      }
      context.LogService.clipboard.log(`Get data: ${data}`);
      return data;
    }

    /**
     * Set the clipboard content
     * @returns {Promise<boolean>}
     */
    async setClipboardData(textToSet) {
      if (!this.isApiAvailable()) {
        return false;
      }

      if (textToSet === null) {
        return false;
      }

      try {
        await this._clipboard.writeText(textToSet);
      } catch (err) {
        context.LogService.clipboard.log(`Failed to set data: ${err}`);
        context.LogService.clipboard.log(`Retry to set data with an user action`);
        let success = false;
        await gbc.alert(`${gbc.I18NService.t("gwc.clipboard.setAccess")}\n${textToSet}`, null, async () => {
          try {
            await this._clipboard.writeText(textToSet);
            success = true;
          } catch (err) {
            context.LogService.clipboard.warn(`Failed to set data after retry: ${err}`);
          }
        });
        if (!success) {
          return false;
        }
      }
      context.LogService.clipboard.log(`Set data: ${textToSet}`);
      return true;
    }

    /**
     * Add text to the current clipboard content
     * @param {string} textToAdd
     * @returns {Promise<boolean>}
     */
    async addToClipboardData(textToAdd) {
      const currentText = await this.getClipboardData();
      return await this.setClipboardData(currentText + textToAdd);
    }
  }

  gbc.ClipboardService = new ClipboardService();
  context.InitService.register(context.ClipboardService);
});
